<template>
  <div data-cy="page-devoir-de-conseil">
    <!-- Contient le retour de devoir-de-conseil/proposition, qui est du contenu HTML -->
    <iframe
      v-if="iframeUrl !== undefined"
      id="iframe"
      :src="iframeUrl"
      style="width:100%;overflow:hidden;"
      @load="onIframeLoaded"
    />

    <!-- En cas du retour utilisé ci-dessus, on désactive cette partie -->
    <div
      v-if="iframeUrl === undefined"
      data-cy="devoir-de-conseil-form"
      class="o-form__spacing"
    >
      <SlHeader :current-step="1" />

      <sl-separator class="mb-4" />

      <!-- Informations personnelles -->
      <personal-information-form
        v-model:civilite="personalInformation.civilite"
        v-model:nom="personalInformation.nom"
        v-model:prenom="personalInformation.prenom"
        v-model:dateDeNaissance="personalInformation.dateDeNaissance"
        v-model:regime="personalInformation.regime"
        v-model:activite="personalInformation.activite"
        v-model:profession="personalInformation.profession"
        v-model:madelin="personalInformation.madelin"
        v-model:displayMadelin="personalInformation.displayMadelin"
        class="mb-4 pb-2"
        :regimes="regimes"
        :server-errors="serverErrors"
      />

      <!-- Autres personnes à assurer -->
      <other-personal-information-form
        v-model:conjoint="otherPersonalInformation.conjoint"
        v-model:nbEnfants="otherPersonalInformation.nbEnfants"
        class="mb-4 pb-2"
        :nb-enfants-options="nbEnfantsOptions"
        :server-errors="serverErrors"
      />

      <!-- Conjoint -->
      <spouse-information-form
        v-if="otherPersonalInformation.conjoint === '1'"
        v-model:civiliteConjoint="spouseInformation.civiliteConjoint"
        v-model:nomConjoint="spouseInformation.nomConjoint"
        v-model:prenomConjoint="spouseInformation.prenomConjoint"
        v-model:dateDeNaissanceConjoint="spouseInformation.dateDeNaissanceConjoint"
        v-model:ayantDroit="spouseInformation.ayantDroit"
        v-model:regimeConjoint="spouseInformation.regimeConjoint"
        v-model:activiteConjoint="spouseInformation.activiteConjoint"
        v-model:professionConjoint="spouseInformation.professionConjoint"
        v-model:madelinConjoint="spouseInformation.madelinConjoint"
        v-model:displayMadelinConjoint="spouseInformation.displayMadelinConjoint"
        class="mb-4 pb-2"
        :regimes="regimes"
        :server-errors="serverErrors"
      />

      <!-- Coordonnées -->
      <contact-information-form
        v-model:adresse="contactInformation.adresse"
        v-model:codePostal="contactInformation.codePostal"
        v-model:ville="contactInformation.ville"
        v-model:email="contactInformation.email"
        v-model:consent="contactInformation.consent"
        v-model:telephone="contactInformation.telephone"
        v-model:optin="contactInformation.optin"
        class="pb-2"
        :server-errors="serverErrors"
      />

      <!-- Expression des besoins et exigences -->
      <needs-information-form
        v-model:couvertureSante="needsInformation.couvertureSante"
        v-model:dateEffet="needsInformation.dateEffet"
        v-model:typeGarantie="needsInformation.typeGarantie"
        v-model:niveauHospi="needsInformation.niveau_hospi"
        v-model:niveauDentaire="needsInformation.niveau_dentaire"
        v-model:niveauOptique="needsInformation.niveau_optique"
        v-model:niveauHonoraire="needsInformation.niveau_honoraire"
        v-model:niveauPrevention="needsInformation.niveau_prevention"
        v-model:besoinObstetrique="needsInformation.besoinObstetrique"
        v-model:besoinOrthodontie="needsInformation.besoinOrthodontie"
        v-model:ancienContrat12MoisAnciennete="needsInformation.ancienContrat12MoisAnciennete"
        v-model:ancienContrat6MoisAnciennete="needsInformation.ancienContrat6MoisAnciennete"
        v-model:dateEffetAncienContrat="needsInformation.dateEffetAncienContrat"
        v-model:montantGarantieImportant="needsInformation.montantGarantieImportant"
        v-model:budgetMensuel="needsInformation.budgetMensuel"
        class="mb-4 pb-2"
        :display-obstetrique-ou-orthodontie="needsInformation.displayObstetriqueOuOrthodontie"
        :server-errors="serverErrors"
        :date-effet-from="dateEffetFrom"
        :date-effet-to="dateEffetTo"
      />

      <FriendlyCaptcha sitekey="FCMVPCK1VET1ETII" @solution-found="onCaptchaSolution"/>

      <sl-button
        data-cy="submit-devoir-conseil"
        @click="submit()"
      >
        Ce que me propose Swisslife
      </sl-button>

      <sl-separator class="mt-4 mb-4" />
    </div>
  </div>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import OtherPersonalInformationForm from "src/modules/devoir-de-conseil/components/OtherPersonalInformationForm"
import PersonalInformationForm from 'src/modules/devoir-de-conseil/components/PersonalInformationForm'
import SpouseInformationForm from "src/modules/devoir-de-conseil/components/SpouseInformationForm";
import ContactInformationForm from "src/modules/devoir-de-conseil/components/ContactInformationForm"
import NeedsInformationForm from "src/modules/devoir-de-conseil/components/NeedsInformationForm";
import { ref, reactive, watch, onMounted, nextTick } from "vue";
import {madelinAvailable} from 'src/modules/devoir-de-conseil/functionnal/madelinAvailable'
import {dateEffetFn, dateEffetFromFn, dateEffetToFn, dateEffetAncienContratFn} from 'src/modules/devoir-de-conseil/functionnal/dateEffet'
import {obstetriqueOuOrthodontieAvailable} from 'src/modules/devoir-de-conseil/functionnal/obstetriqueOuOrthodontieAvailable'
import DevoirDeConseilService from 'src/modules/devoir-de-conseil/services/devoir-de-conseil.service'
import {nxtcmsDateConverter} from "src/modules/shared/utils/dateUtils";
import SlButton from "src/modules/shared/components/SlButton";
import SlSeparator from "src/modules/shared/components/SlSeparator";
import {NXTCMS_URL } from "src/modules/shared/config/config.service"
import useDialog from "src/modules/shared/composable/useDialog";
import dayjs from "dayjs";
import SlHeader from "src/modules/shared/components/SlHeader";
import FriendlyCaptcha from "../../shared/components/FriendlyCaptcha";

export default {
  name: 'Form',
  components: {
    FriendlyCaptcha,
    ContactInformationForm,
    NeedsInformationForm,
    OtherPersonalInformationForm,
    PersonalInformationForm,
    SpouseInformationForm,
    SlButton,
    SlHeader,
    SlSeparator
  },
  emits: [
    'goto'
  ],
  setup() {
    const initialState = window.__INITIAL_STATE__ || {}
    const {showLoaderLoading, showLoaderSending, showPopinError, hideLoader} = useDialog()
    const iframeUrl = ref(initialState.redirect ?? undefined)
    const customUserData = reactive({})

    const personalInformation = reactive({
      civilite: '',
      nom: '',
      prenom: '',
      dateDeNaissance: '',
      regime: '',
      activite: '',
      profession: '',
      madelin: '',
      displayMadelin: '',
    })

    const otherPersonalInformation = reactive({
      conjoint: '2',
      nbEnfants: '0',
    })

    const spouseInformation = reactive({
      civiliteConjoint: '',
      nomConjoint: '',
      prenomConjoint:  '',
      dateDeNaissanceConjoint: '',
      ayantDroit: '',
      regimeConjoint: '',
      activiteConjoint: '',
      professionConjoint: '',
      madelinConjoint: '',
      displayMadelinConjoint: '',
    })

    const contactInformation = reactive({
      adresse: '',
      codePostal: '',
      ville: '',
      email: '',
      consent: '',
      telephone: '',
      optin: '',
    })

    const needsInformation = reactive({
      couvertureSante: '0', // false
      besoinObstetrique: '0', // false
      besoinOrthodontie: '0', // false
      dateEffet: '',
      typeGarantie: '',
      niveau_hospi: '',
      niveau_dentaire: '50',
      niveau_optique: '50',
      niveau_honoraire: '50',
      niveau_prevention: '50',
      displayObstetriqueOuOrthodontie: '',
      essentielPharma: '0',
      ancienContrat12MoisAnciennete: '',
      ancienContrat6MoisAnciennete: '',
      dateEffetAncienContrat: '',
      montantGarantieImportant: '',
      budgetMensuel: '',
    })

    const serverErrors = ref([])
    const dateEffetFrom = ref(dateEffetFromFn(needsInformation.couvertureSante))
    const dateEffetTo = ref(dateEffetToFn())

    const captchaSolution = ref('')

    // Gère l'option madelin
    const displayMadelinWatcher = watch(
        [
          () => personalInformation.activite,
          () => personalInformation.dateDeNaissance,
          () => needsInformation.typeGarantie
        ], () => {
          console.debug("[Form]", "displayMadelinWatcher")
          personalInformation.displayMadelin = madelinAvailable(
              personalInformation.activite,
              personalInformation.dateDeNaissance,
              needsInformation.typeGarantie,
          )
          console.debug("[Form]", "displayMadelinWatcher", personalInformation.displayMadelin)
        }
    )

    // Gère l'option madelinConjoint
    const displayMadelinConjointWatcher = watch(
        [
          () => spouseInformation.activiteConjoint,
          () => spouseInformation.dateDeNaissanceConjoint,
          () => needsInformation.typeGarantie,
        ], () => {
          console.debug("[Form]", "displayMadelinConjointWatcher")
          spouseInformation.displayMadelinConjoint = madelinAvailable(
              spouseInformation.activiteConjoint,
              spouseInformation.dateDeNaissanceConjoint,
              needsInformation.typeGarantie,
          )
          console.debug("[Form]", "displayMadelinConjointWatcher", spouseInformation.displayMadelinConjoint)
        }
    )

    // Gère l'option obstetrique et orthodontie
    const displayObstetriqueOuOrthodontieWatcher = watch(
        [
          () => personalInformation.activite,
          () => personalInformation.dateDeNaissance
        ], () => {
          console.debug("[Form]", "displayObstetriqueOuOrthodontieWatcher")

          needsInformation.displayObstetriqueOuOrthodontie = obstetriqueOuOrthodontieAvailable(
              personalInformation.activite,
              personalInformation.dateDeNaissance
          )

          console.debug("[Form]", "displayObstetriqueOuOrthodontieWatcher", needsInformation.displayObstetriqueOuOrthodontie)
        }
    )

    // Affiche une popup d'erreur en cas d'ancien contrat < à 6 mois
    const ancienContrat6MoisAncienneteWatcher = watch(
      [() => needsInformation.ancienContrat6MoisAnciennete],
        () => {
          console.debug("[Form]", "ancienContrat6MoisAncienneteWatcher")
          if (needsInformation.ancienContrat6MoisAnciennete === '0') {
            showPopinError('Nous ne pouvons vous proposer de devis santé actuellement compte tenu de votre situation')
          }
        }
    );

    const couvertureSanteWatcher = watch([
      () => needsInformation.couvertureSante
    ], () => {
      console.debug('[DevoirDeConseil]', 'couvertureSanteWatcher')
      dateEffetFrom.value = dateEffetFromFn(needsInformation.couvertureSante)
      console.debug('[DevoirDeConseil]', 'couvertureSanteWatcher', 'dateEffetFrom', dateEffetFrom.value)
    })

    const dateEffetWatcher = watch(
        [
          () => needsInformation.dateEffet,
          () => needsInformation.couvertureSante
        ], () => {
          console.debug("[Form]", "dateEffet")
          needsInformation.dateEffet = dateEffetFn(
              needsInformation.dateEffet,
              needsInformation.couvertureSante
          )
          console.debug("[Form]", "dateEffet", needsInformation.dateEffet)
        }
    )

    // RIA
    // Si dateEffetAncienContract > yesterday => dateEffetAncienContract = yesterday
    const dateEffetAncienContratWatcher = watch(
        [
          () => needsInformation.dateEffetAncienContrat
        ], () => {
          console.debug("[Form]", "dateEffetAncienContrat")
          needsInformation.dateEffetAncienContrat = dateEffetAncienContratFn(needsInformation.dateEffetAncienContrat)
          console.debug("[Form]", "dateEffetAncienContrat", needsInformation.dateEffetAncienContrat)
        }
    )

    const regimes = ref({})
    const nbEnfantsOptions = ref([])

    onMounted(async () => {
      await DevoirDeConseilService.getUser().then(async res => {
        console.debug('DevoirDeConseil', 'getUser', res.data)

        customUserData.value = res.data

        personalInformation.civilite = customUserData.value.civilite ?? ''
        personalInformation.nom = customUserData.value.nom ?? ''
        personalInformation.prenom = customUserData.value.prenom ?? ''
        personalInformation.dateDeNaissance = nxtcmsDateConverter(customUserData.value.dateDeNaissance) ?? ''
        personalInformation.regime = customUserData.value.regime ?? ''
        await nextTick() // besoin d'attendre le watcher sur le regime
        personalInformation.activite = customUserData.value.activite ?? ''
        await nextTick() // besoin d'attendre le watcher sur la profession
        personalInformation.profession = customUserData.value.profession ?? ''

        personalInformation.madelin = customUserData.value.madelin ?? ''
        personalInformation.displayMadelin = customUserData.value.displayMadelin ?? '0'

        otherPersonalInformation.conjoint = customUserData.value.conjoint ?? '2'
        otherPersonalInformation.nbEnfants = customUserData.value.nbEnfants ?? '0'

        spouseInformation.civiliteConjoint = customUserData.value.civiliteConjoint ?? ''
        spouseInformation.nomConjoint = customUserData.value.nomConjoint ?? ''
        spouseInformation.prenomConjoint = customUserData.value.prenomConjoint ?? ''
        spouseInformation.dateDeNaissanceConjoint = nxtcmsDateConverter(customUserData.value.dateDeNaissanceConjoint) ?? ''
        spouseInformation.ayantDroit = customUserData.value.ayantDroit ?? ''
        spouseInformation.regimeConjoint = customUserData.value.regimeConjoint ?? ''
        await nextTick() // besoin d'attendre le watcher sur le regime
        spouseInformation.activiteConjoint = customUserData.value.activiteConjoint ?? ''
        await nextTick() // besoin d'attendre le watcher sur la profession
        spouseInformation.professionConjoint = customUserData.value.professionConjoint ?? ''
        spouseInformation.madelinConjoint = customUserData.value.madelinConjoint ?? ''
        spouseInformation.displayMadelinConjoint = customUserData.value.displayMadelinConjoint ?? '0'

        contactInformation.adresse = customUserData.value.adresse ?? ''
        contactInformation.codePostal = customUserData.value.codePostal ?? ''
        contactInformation.ville = customUserData.value.ville ?? ''
        contactInformation.email = customUserData.value.email ?? ''
        contactInformation.consent = customUserData.value.consent ?? ''
        contactInformation.telephone = customUserData.value.telephone ?? ''
        contactInformation.optin = customUserData.value.optin ?? ''

        needsInformation.couvertureSante = customUserData.value.couvertureSante ?? '0'
        needsInformation.besoinObstetrique = customUserData.value.besoinObstetrique ?? '0'
        needsInformation.besoinOrthodontie = customUserData.value.besoinOrthodontie ?? '0'
        needsInformation.dateEffet = nxtcmsDateConverter(customUserData.value.dateEffet) ?? dayjs().format('DD/MM/YYYY')
        needsInformation.typeGarantie = customUserData.value.typeGarantie ?? ''
        needsInformation.niveau_hospi = customUserData.value.niveau_hospi ?? '0'
        needsInformation.niveau_dentaire = customUserData.value.niveau_dentaire ?? '50'
        needsInformation.niveau_optique = customUserData.value.niveau_optique ?? '50'
        needsInformation.niveau_honoraire = customUserData.value.niveau_honoraire ?? '50'
        needsInformation.niveau_prevention = customUserData.value.niveau_prevention ?? '50'
        needsInformation.displayObstetriqueOuOrthodontie = customUserData.value.displayObstetriqueOuOrthodontie ?? ''
        needsInformation.essentielPharma = customUserData.value.essentielPharma ?? '0'
        needsInformation.ancienContrat12MoisAnciennete = customUserData.value.ancienContrat12MoisAnciennete ?? ''
        needsInformation.ancienContrat6MoisAnciennete = customUserData.value.ancienContrat6MoisAnciennete ?? ''
        needsInformation.dateEffetAncienContrat = customUserData.value.dateEffetAncienContrat ?? ''
        needsInformation.montantGarantieImportant = customUserData.value.montantGarantieImportant ?? ''
        needsInformation.budgetMensuel = customUserData.value.budgetMensuel ?? ''
      })

      await DevoirDeConseilService.getParameters().then((res) => {
        // On récupère les régimes disponibles
        regimes.value = res.data.regimes

        // On récupère les nbEnfants disponibles
        for (let[key, val] of Object.entries(res.data.nbEnfants)) {
          nbEnfantsOptions.value.push({label: val, value: key})
        }
      })

      /**
       * Récupère le "message" de l'iframe contenant sa taille pour la fixer correctement
       */
      window.addEventListener("message", (event) => {
        // console.error(event.origin, event.data)
        if(event.data.action === 'navigate') {
          // On met à jour l'iframe
          let iframe = document.querySelector('#iframe');
          iframe.src = event.data.url;
        }

        if (event.origin !== NXTCMS_URL) {
          let iframe = document.querySelector('#iframe');
          if (iframe) {
            iframe.height = event.data.height + 50;
          }
        }
      });
    })

    return {
      v$: useVuelidate(),
      initialState,
      personalInformation,
      otherPersonalInformation,
      spouseInformation,
      contactInformation,
      needsInformation,
      displayMadelinWatcher,
      displayMadelinConjointWatcher,
      displayObstetriqueOuOrthodontieWatcher,
      ancienContrat6MoisAncienneteWatcher,
      couvertureSanteWatcher,
      dateEffetWatcher,
      dateEffetFrom,
      dateEffetTo,
      dateEffetAncienContratWatcher,
      regimes,
      nbEnfantsOptions,
      iframeUrl,
      serverErrors,
      showLoaderLoading,
      showLoaderSending,
      showPopinError,
      hideLoader,
      captchaSolution
    }
  },
  async mounted() {
    const initialState = window.__INITIAL_STATE__
    console.debug("[DevoirDeConseil]", "initialState", initialState)
  },
  methods: {
    onIframeLoaded() {
      this.hideLoader()

      // Après le chargement de l'iframe contenant le tarificateur,
      // Envoie un message pour indiquer quel tableau afficher par défaut
      let iframe = document.querySelector('#iframe');
      iframe.contentWindow.postMessage(
          {
            key:'tableau_par_default',
            value: window.__INITIAL_STATE__.tableau_par_default
          },
          '*'
      );
    },
    onCaptchaSolution(solution) {
      this.captchaSolution = solution
    },
    async submit() {
      console.debug("[DevoirDeConseil]", "submit")
      console.debug(this.personalInformation, this.needsInformation)

      // On reset les erreurs serveurs
      this.serverErrors = []
      await nextTick()

      // On valide les formulaires
      this.v$.$touch()
      console.debug('VUE TOUCH', this.v$)

      // Hack : On regarde si on a uniquement des erreurs serveurs car elles ne partent pas d'elles-mêmes...
      let onlyServerError = true
      for (const error of this.v$.$errors) {
        if (error.$validator !== 'serverError') onlyServerError = false
      }

      // En cas d'erreur, on ne fait rien (les composants vont afficher d'eux-mêmes les erreurs)
      if (this.v$.$error && !onlyServerError) return

      // Vérification pour Complémentaire Santé Solidaire
      if (this.needsInformation.typeGarantie === '3' || this.needsInformation.typeGarantie === '2') {
        this.showPopinError('Aucune offre ne correspond à votre simulation')
        return
      }

      // Début des envois
      await this.showLoaderSending()

      // En cas de succès, on récupère les différentes données des composants pour les envoyer
      const data = {
        ...this.personalInformation,
        ...this.otherPersonalInformation,
        ...this.spouseInformation,
        ...this.contactInformation,
        ...this.needsInformation,
        captcha: this.captchaSolution
      }

      const param = {
        zone: this.initialState.zoneId,
        page: this.initialState.pageId,
        iframe_referer: this.initialState.iframe_referer
      }


      console.debug("[DevoirDeConseil]", "submit", "param", param)

      this.iframeUrl = await DevoirDeConseilService.submitForm(data, param).then(res => {
        console.debug("[DevoirDeConseil]", "submitForm", "res", res)

        return DevoirDeConseilService.getProposition(data,param).then(res => {
          console.debug("[DevoirDeConseil]", "getProposition", "res", res)

          // Récupère une url qui sera chargé dans une iframe... Pas le choix de faire autrement...
          return res.data.redirect_url
        })

      }).catch(error => {
        console.error("[DevoirDeConseil]", "submit", "error", error.response.data.msg)

        // Si on détecte des erreurs serveurs, on met la variable à jour
        if (error.response.data.violations !== undefined) {
          this.serverErrors = error.response.data.violations
          console.error(this.serverErrors)
        } else {
          const errorMsg = error.response.data.msg ?? 'Erreur serveur'
          this.showPopinError(errorMsg)
        }
      }).finally(() => {
        this.hideLoader()
      })

      if (this.iframeUrl) {
        this.showLoaderLoading()
      }

    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
h3 {
  margin: 40px 0 0;
}
ul {
  list-style-type: none;
  padding: 0;
}
li {
  display: inline-block;
  margin: 0 10px;
}
a {
  color: #42b983;
}
#app .o-form__spacing {
  padding-left: 24px;
  padding-right: 24px;
  padding-top: 32px;
  padding-bottom: 32px;
  background-color: white;
}

iframe::-webkit-scrollbar {
  display: none;
}
</style>
